'use client';

import { PropsWithClassname } from '@/app/types';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import { useCurrencyTranslation } from '@/app/hooks/useCurrencyTranslation';

import { DonationMatching } from '@/app/api';
import { formatNumber, formatPriceInCents } from '@/app/utils/formatting';
import CountUp from 'react-countup';

interface FinishedProjectSummaryProps extends PropsWithClassname {
  currentValueInCents: number;
  donationsCount?: number;
  matching?: DonationMatching;
}

const FinishedProjectSummary = ({
  donationsCount,
  currentValueInCents,
  className,
  matching,
}: FinishedProjectSummaryProps) => {
  const { t } = useTranslation();
  const currencyTranslation = useCurrencyTranslation();

  return (
    <div className={classNames(className)}>
      <div className="flex">
        <div className="grow">
          <span className="inline-block text-[20px] whitespace-nowrap tabular-nums">
            {t('components.FinishedProjectSummary.collected')}{' '}
            <span className="font-medium">
              <Trans
                i18nKey={'components.FinishedProjectSummary.currentAmount'}
                values={{ currency: currencyTranslation }}
              >
                <CountUp start={0} end={currentValueInCents / 100} duration={2} separator=" " />
              </Trans>
            </span>
          </span>
          {!!donationsCount && !matching && (
            <span className="block text-[14px] text-neutral-semiTransparent">
              {t('components.FinishedProjectSummary.donationsCount', {
                count: donationsCount,
              })}
            </span>
          )}
          {matching && currentValueInCents > 0 && (
            <>
              <span className="block text-[14px] text-neutral-semiTransparent">
                <span>{formatPriceInCents(currentValueInCents - matching.currentAmountInCents)}</span>{' '}
                {t('components.ProjectProgress.donationsCount', {
                  count: donationsCount,
                  value: formatNumber(donationsCount || 0),
                })}
              </span>
              <span className="block text-[14px] text-neutral-semiTransparent">
                {t('components.ProjectProgress.donationFromMatchedCompany', {
                  currentAmountInCents: formatPriceInCents(matching.currentAmountInCents),
                  companyName: matching.name,
                })}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinishedProjectSummary;
